import { createSelector } from 'reselect';
import { State } from 'flow/types/State';
import { List, Map } from 'immutable';
import {
    Favorite,
    FavoriteEntityKey,
    FavoriteRecord,
    FavoriteType,
} from 'flow/types/Favorite';
import { TypedMap, createTypedMap } from 'flow/types/TypedMap';
import {
    ContactKey,
    ExtensionKey,
    FavoriteKey,
    QueueKey,
} from 'flow/types/EntityKeys';
import { isContactKey, isExtensionKey, isQueueKey } from 'flow/utils';
import { selectWaitingQueueItem } from 'flow/selectors/queue-selectors';
import {
    getQueueDescriptionFromQueueId,
    getQueueIsOfTypeOmni,
} from 'flow/selectors/selectors';
import { sortQueueItems } from 'flow/utils/pbx-sorting-utils';

const selectFavorites = (state: State) => state.favorites;

export const selectFavoritesOfType = (
    favorites: TypedMap<Record<FavoriteKey, TypedMap<Favorite>>>,
    favoriteType: FavoriteType,
) => {
    if (!favorites) {
        //We have to cast here bc ImmutableJS Map() doesn't conform to our type. And we need it to this type to avoid issues in further selectors below
        return createTypedMap<Record<FavoriteKey, TypedMap<Favorite>>>();
    }
    return favorites.filter(
        (favorite) => favorite.get('type') === favoriteType,
    );
};

export const selectExtensionFavorites = createSelector(
    [selectFavorites],
    (favorites) => selectFavoritesOfType(favorites, 'extension'),
);

export const selectContactFavorites = createSelector(
    [selectFavorites],
    (favorites) => selectFavoritesOfType(favorites, 'contact'),
);

export const selectQueueFavorites = createSelector(
    [selectFavorites],
    (favorites) => selectFavoritesOfType(favorites, 'queue'),
);

export const selectExtensionAndContactFavorites = createSelector(
    [selectFavorites],
    (favorites) => {
        if (!favorites) {
            return createTypedMap<Record<FavoriteKey, TypedMap<Favorite>>>();
        }

        return favorites.filter((favorite) =>
            ['extension', 'contact'].includes(favorite.get('type')),
        );
    },
);

export const selectFavoriteFromEntityKey = (
    entityKey: FavoriteEntityKey,
    favorites: FavoriteRecord,
) => {
    if (!favorites) {
        return undefined;
    }

    return favorites.find(
        (favorite) => favorite.getIn(['favoriteData', 'key']) === entityKey,
    );
};

export const selectFavoriteKeyFromEntityKey = (
    entityKey: FavoriteEntityKey,
    favorites: FavoriteRecord,
) => {
    const favorite = selectFavoriteFromEntityKey(entityKey, favorites);
    return favorite ? favorite.get('key') : undefined;
};

const selectFavoriteData = (
    favoriteTypeKey: FavoriteEntityKey,
    state: State,
) => {
    if (isExtensionKey(favoriteTypeKey)) {
        return state.extensions.getIn([
            'extensions',
            favoriteTypeKey as ExtensionKey,
        ]);
    } else if (isContactKey(favoriteTypeKey)) {
        const key = favoriteTypeKey as ContactKey;
        const contacts = state.contacts;
        return (
            contacts.getIn(['externalContacts', key]) ??
            contacts.getIn(['flowContacts', key])
        );
    } else if (isQueueKey(favoriteTypeKey)) {
        return state.pbx.getIn(['queues', favoriteTypeKey as QueueKey]);
    } else {
        console.warn('Unknown favoriteTypeKey:', favoriteTypeKey);
        return {
            key: favoriteTypeKey,
        };
    }
};
export default selectFavoriteData;

export const selectWaitingItemsInQueuesMarkedAsFavoriteOldestFirst = (
    state: State,
) => {
    const favoriteQueues = selectQueueFavorites(state);
    const waitingQueueItemsToReturn: TypedMap<Favorite>[] = [];

    const waitingQueueItemsByFavoriteQueue = state.pbx
        .getIn(['waitingQueueItems', 'byQueue'], Map())
        .filter(
            (_item, queueKey) =>
                !!favoriteQueues.find(
                    (favQueue) =>
                        favQueue.getIn(['favoriteData', 'key']) === queueKey,
                ),
        );

    waitingQueueItemsByFavoriteQueue.forEach((queue, queueKey) => {
        queue.forEach((queueCallKey, index) => {
            const queuedItem =
                selectWaitingQueueItem(state, queueCallKey) || Map();
            const queueDescription = getQueueDescriptionFromQueueId(
                state,
                queueKey,
            );
            const queueItemWithAdditionalData = queuedItem.merge({
                queuePosition: index + 1,
                queueDescription: queueDescription,
                itemIsInQueueSupportingOmni: getQueueIsOfTypeOmni(
                    state,
                    queueKey,
                ),
            });
            //@ts-ignore
            waitingQueueItemsToReturn.push(queueItemWithAdditionalData);
        });
    });

    return sortQueueItems(List(waitingQueueItemsToReturn));
};

export const selectKeysForOngoingQueueCallsInQueuesMarkedAsFavorite =
    createSelector(
        (state) => state.pbx,
        selectQueueFavorites,
        (pbx, favoriteQueues) => {
            const keysToReturn: (string | number)[] = [];

            const activeQueueCallsByFavoriteQueue = pbx
                .getIn(['topActiveQueueCalls', 'byQueue'], Map())
                .filter((_calls, queueKey) => {
                    return !!favoriteQueues.find(
                        (favQueue) =>
                            favQueue.getIn(['favoriteData', 'key']) ===
                            queueKey,
                    );
                });

            activeQueueCallsByFavoriteQueue.forEach((calls) => {
                calls.forEach((status, callKey) => {
                    if (status === 'Up') {
                        keysToReturn.push(callKey);
                    }
                });
            });

            return List(keysToReturn);
        },
    );
